
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const PartnersPreview = () => {
  // Use a dynamic timestamp to ensure all instances use the latest value
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const isMobile = useIsMobile();
  const mountedRef = useRef(false);
  
  useEffect(() => {
    console.log(`PartnersPreview: Ensuring content is fresh (mobile: ${isMobile})`);
    
    // Force fresh content by updating the key
    const timestamp = Date.now();
    setRefreshKey(timestamp);
    
    // Consistent attribute for version tracking
    document.documentElement.setAttribute('data-partners-preview-version', timestamp.toString());
    
    // Set a unified approach to refreshing images
    const refreshImages = () => {
      console.log(`Refreshing partner preview images at ${timestamp}`);
      document.querySelectorAll('img').forEach(img => {
        if (img.src && !img.src.includes('data:')) {
          const srcBase = img.src.split('?')[0];
          // Use consistent query parameters across all devices
          img.src = `${srcBase}?v=${timestamp}&device=${isMobile ? 'mobile' : 'desktop'}`;
          // Add version attribute for debugging
          img.setAttribute('data-version', timestamp.toString());
        }
      });
    };
    
    // Run immediate refresh
    refreshImages();
    
    // Handle first mount with additional refreshes
    if (!mountedRef.current) {
      mountedRef.current = true;
      
      // Schedule multiple refreshes to ensure content is loaded correctly
      const refreshTimers = [100, 300, 1000].map(delay => 
        setTimeout(refreshImages, delay)
      );
      
      return () => {
        refreshTimers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [isMobile]);
  
  return (
    <section 
      className="py-8 xs:py-12 md:py-16 relative overflow-hidden" 
      data-component="partners-preview"
      data-version={refreshKey}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl xs:text-3xl md:text-4xl font-bold mb-3 xs:mb-4">Meet Our Team</h2>
          <p className="text-delphi-light/70 text-sm xs:text-base sm:text-lg">
            Our leadership combines expertise in physics, mathematics, and finance to push the boundaries of what's possible.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xs:gap-8">
          <div className="data-section p-4 xs:p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/3">
                <div className="aspect-square rounded-md overflow-hidden bg-gradient-to-br from-delphi-gray/10 to-transparent">
                  <img 
                    src={`/lovable-uploads/115b0274-82e7-436d-a480-977cd2f65ed1.png?v=${refreshKey}`} 
                    alt="Carson King" 
                    className="w-full h-full object-cover object-top"
                    data-version={refreshKey}
                  />
                </div>
              </div>
              <div className="md:w-2/3">
                <h3 className="text-xl font-bold mb-1">Carson King</h3>
                <p className="text-delphi-red mb-3">Co-Founder</p>
                <p className="text-xs xs:text-sm text-delphi-light/70 mb-4">
                  Physics researcher specializing in magnetohydrodynamic imaging algorithms with experience managing $1.2M in trading capital.
                </p>
                <Link to="/partners" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
                  <span>Learn More</span>
                  <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
          
          <div className="data-section p-4 xs:p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/3">
                <div className="aspect-square rounded-md overflow-hidden bg-gradient-to-br from-delphi-gray/10 to-transparent">
                  <img 
                    src={`/lovable-uploads/228c76f2-25fc-4950-8046-96ff988c292d.png?v=${refreshKey}`} 
                    alt="Marcus DuPont" 
                    className="w-full h-full object-cover object-top"
                    data-version={refreshKey}
                  />
                </div>
              </div>
              <div className="md:w-2/3">
                <h3 className="text-xl font-bold mb-1">Marcus DuPont, Ph.D.</h3>
                <p className="text-delphi-red mb-3">Co-Founder</p>
                <p className="text-xs xs:text-sm text-delphi-light/70 mb-4">
                  Princeton researcher and first Black Ph.D. in Physics at NYU, combining expertise in astrophysics with quantum mechanics.
                </p>
                <Link to="/partners" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
                  <span>Learn More</span>
                  <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 sm:mt-10 md:mt-12 text-center">
          <Link 
            to="/partners" 
            className="inline-flex items-center px-5 py-2.5 xs:px-6 xs:py-3 border border-delphi-gray/30 rounded-md text-delphi-light hover:bg-delphi-gray/10 transition-all"
            data-version={refreshKey}
          >
            <span>Meet the Full Team</span>
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PartnersPreview;
